import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../../core/_internal/factory.js';
import { Card } from '../../core/Card/Card.js';
import { Group } from '../../core/Group/Group.js';
import { Stack } from '../../core/Stack/Stack.js';
import { Text } from '../../core/Text/Text.js';
import { Title } from '../../core/Title/Title.js';
import { Loader } from '../../core/Loader/Loader.js';
import { TextInput } from '../../inputs/TextInput/TextInput.js';
import { SegmentedControl } from '../../inputs/SegmentedControl/SegmentedControl.js';
import { Select } from '../../inputs/Select/Select.js';
import { Pagination } from '../Pagination/Pagination.js';
import './TableShell.css';

export type ViewMode = 'table' | 'cards' | 'list';

const svg = (d: ReactNode) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {d}
  </svg>
);

const TableIcon = svg(<>
  <rect x="1.5" y="2" width="11" height="10" rx="1.2" />
  <line x1="1.5" y1="5.5" x2="12.5" y2="5.5" />
  <line x1="1.5" y1="8.75" x2="12.5" y2="8.75" />
  <line x1="5.5" y1="5.5" x2="5.5" y2="12" />
</>);

const GridIcon = svg(<>
  <rect x="1.8" y="1.8" width="4.4" height="4.4" rx="0.9" />
  <rect x="7.8" y="1.8" width="4.4" height="4.4" rx="0.9" />
  <rect x="1.8" y="7.8" width="4.4" height="4.4" rx="0.9" />
  <rect x="7.8" y="7.8" width="4.4" height="4.4" rx="0.9" />
</>);

const ListIcon = svg(<>
  <circle cx="2.6" cy="3.2" r="0.8" fill="currentColor" stroke="none" />
  <line x1="5" y1="3.2" x2="12.5" y2="3.2" />
  <circle cx="2.6" cy="7" r="0.8" fill="currentColor" stroke="none" />
  <line x1="5" y1="7" x2="12.5" y2="7" />
  <circle cx="2.6" cy="10.8" r="0.8" fill="currentColor" stroke="none" />
  <line x1="5" y1="10.8" x2="12.5" y2="10.8" />
</>);

/**
 * Every visible word this component owns. They were English literals before,
 * unreachable from a page, which meant the design system itself broke the rule
 * that all copy comes from a catalogue. Defaults keep existing call sites
 * working; a translated console passes its own.
 */
export interface TableShellLabels {
  table: string;
  cards: string;
  list: string;
  search: string;
  perPage: string;
  /** Rendered as `${n} / page` by default; receives the page size. */
  pageSizeOption: (n: number) => string;
  empty: string;
  /** Receives the visible range and the total. */
  range: (from: number, to: number, total: number) => string;
}

const DEFAULT_LABELS: TableShellLabels = {
  table: 'Table',
  cards: 'Grid',
  list: 'List',
  search: 'Search',
  perPage: 'Rows per page',
  pageSizeOption: (n) => `${n} / page`,
  empty: 'No records',
  range: (from, to, total) => `${from}–${to} of ${total}`
};

function buildViewData(labels: TableShellLabels): Array<{ value: ViewMode; label: ReactNode }> {
  return [
    { value: 'table', label: <span className="rr-table-shell__view-label" title={labels.table}>{TableIcon}<span>{labels.table}</span></span> },
    { value: 'cards', label: <span className="rr-table-shell__view-label" title={labels.cards}>{GridIcon}<span>{labels.cards}</span></span> },
    { value: 'list',  label: <span className="rr-table-shell__view-label" title={labels.list}>{ListIcon}<span>{labels.list}</span></span> }
  ];
}

export interface TableShellProps extends BaseProps {
  /** Section title rendered in the toolbar. */
  title?: ReactNode;
  /** Short description below the title. */
  description?: ReactNode;
  /** Result count chip (e.g. "12 of 124"). */
  totalLabel?: ReactNode;
  /** KPI / metric row rendered above the toolbar. Typically `<KpiFilterRow>`. */
  kpis?: ReactNode;
  /**
   * Rendered between the toolbar and the table. Its purpose is the row of
   * removable filter pills: they belong under the control that set them, and
   * page after page was putting them above the card instead because there was
   * nowhere else for them to go.
   */
  belowToolbar?: ReactNode;
  /** Search input value. Omit to hide the search field. */
  search?: string;
  /** Search input change handler. */
  onSearch?: (value: string) => void;
  /** Search input placeholder. */
  searchPlaceholder?: string;
  /** Overrides for the words this component owns, so a translated console can supply its own. */
  labels?: Partial<TableShellLabels>;
  /** Extra controls rendered in the toolbar between search and view switcher (e.g. a "Filters" button). */
  filters?: ReactNode;
  /** Right-aligned action buttons (e.g. "+ Add"). */
  actions?: ReactNode;
  /** When provided, renders a SegmentedControl for switching layouts. */
  view?: ViewMode;
  onViewChange?: (view: ViewMode) => void;
  /** Restrict the views the switcher offers. Defaults to all three. */
  availableViews?: ViewMode[];
  /** Loading state; shows an overlay over the content. */
  loading?: boolean;
  /** Empty-state node: rendered in place of children when records are empty. */
  empty?: ReactNode;
  /** Error-state node: rendered in place of children when an error occurred. */
  error?: ReactNode;
  /** When true, signal to consumers that the records collection is empty. */
  isEmpty?: boolean;
  /** Children: typically a `<DataTable>`, `<EntityCardGrid>`, or a list. */
  children?: ReactNode;

  /* ── Pagination (controlled) ──────────────────────────────────────── */
  /** Current 1-based page index. Omit to hide pagination footer. */
  page?: number;
  /** Total number of records (drives total pages). */
  totalRecords?: number;
  /** Rows per page. Default 25. */
  recordsPerPage?: number;
  /** Page change handler. */
  onPageChange?: (page: number) => void;
  /** Page-size change handler. When provided, renders a size selector. */
  onPageSizeChange?: (size: number) => void;
  /** Available page sizes for the selector. Default `[10, 25, 50, 100]`. */
  pageSizeOptions?: number[];

  /** Custom pagination element: overrides the built-in pagination footer. */
  pagination?: ReactNode;
  /** Removes the wrapping Card chrome (default false). */
  noChrome?: boolean;
}

/**
 * Standard list-page chrome.
 *
 *   ┌──── kpis (optional row of clickable KPIs / filters) ─────────────┐
 *   │ toolbar: title · search · filters · view-switch · actions        │
 *   │ belowToolbar: active filter pills                                │
 *   │ body: <DataTable> or <EntityCardGrid> or list                    │
 *   │ footer: pagination · "{from}-{to} of {total}" · page size        │
 *   └──────────────────────────────────────────────────────────────────┘
 */
export const TableShell = forwardRef(function TableShell(
  {
    title, description, totalLabel, kpis, belowToolbar,
    search, onSearch, searchPlaceholder = 'Search…',
    filters, actions,
    view, onViewChange, availableViews,
    loading, empty, error, isEmpty,
    children,
    page, totalRecords, recordsPerPage = 25, onPageChange, onPageSizeChange,
    pageSizeOptions = [10, 25, 50, 100],
    pagination, noChrome, labels: labelOverrides,
    ...rest
  }: TableShellProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };
  const allViews = buildViewData(labels);
  const base = useBaseAttrs('rr-table-shell', rest);
  const { dom } = splitBaseProps(rest);

  const viewData = availableViews
    ? allViews.filter((v) => availableViews.includes(v.value))
    : allViews;

  const toolbar = (
    <div className="rr-table-shell__toolbar">
      {(title || description || totalLabel) && (
        <div className="rr-table-shell__heading">
          {title ? <Title order={4} className="rr-table-shell__title">{title}</Title> : null}
          {description ? <Text role="bodySmall" className="rr-table-shell__description">{description}</Text> : null}
          {totalLabel ? <Text role="caption" className="rr-table-shell__total">{totalLabel}</Text> : null}
        </div>
      )}
      <Group gap="sm" wrap="wrap" justify="flex-end" className="rr-table-shell__controls">
        {onSearch !== undefined ? (
          <TextInput
            size="xs"
            value={search ?? ''}
            onChange={(e) => onSearch(e.currentTarget.value)}
            placeholder={searchPlaceholder}
            aria-label={labels.search}
            className="rr-table-shell__search"
            leftSection={(
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
                <circle cx="5.5" cy="5.5" r="3.5" />
                <line x1="8.5" y1="8.5" x2="12" y2="12" />
              </svg>
            )}
          />
        ) : null}
        {filters}
        {view !== undefined && onViewChange ? (
          <SegmentedControl
            size="xs"
            value={view}
            onChange={(v) => onViewChange(v as ViewMode)}
            data={viewData}
          />
        ) : null}
        {actions}
      </Group>
    </div>
  );

  const body = (
    <div className="rr-table-shell__body">
      {error ? (
        <div className="rr-table-shell__error">{error}</div>
      ) : isEmpty && !loading ? (
        <div className="rr-table-shell__empty">{empty}</div>
      ) : (
        children
      )}
      {loading ? (
        <div className="rr-table-shell__loading" aria-live="polite" aria-busy>
          <Loader size="sm" />
        </div>
      ) : null}
    </div>
  );

  // Build the pagination footer either from the explicit `pagination` slot or
  // from the controlled `page` / `totalRecords` props.
  let footer: ReactNode = null;
  if (pagination) {
    footer = <div className="rr-table-shell__footer">{pagination}</div>;
  } else if (page !== undefined && totalRecords !== undefined && onPageChange) {
    const total = Math.max(0, totalRecords);
    const totalPages = Math.max(1, Math.ceil(total / recordsPerPage));
    const from = total === 0 ? 0 : (page - 1) * recordsPerPage + 1;
    const to = Math.min(total, page * recordsPerPage);

    footer = (
      <div className="rr-table-shell__footer">
        <Group gap="sm" align="center">
          <Text role="caption" className="rr-table-shell__range">
            {total === 0 ? labels.empty : labels.range(from, to, total)}
          </Text>
          {onPageSizeChange ? (
            <Select
              size="xs"
              value={String(recordsPerPage)}
              onChange={(e) => onPageSizeChange(Number(e.currentTarget.value))}
              data={pageSizeOptions.map((n) => ({ value: String(n), label: labels.pageSizeOption(n) }))}
              aria-label={labels.perPage}
              style={{ width: 120 }}
            />
          ) : null}
        </Group>
        <Pagination total={totalPages} value={page} onChange={onPageChange} size="sm" />
      </div>
    );
  }

  const inner = (
    <Stack gap="md">
      {kpis ? <div className="rr-table-shell__kpis">{kpis}</div> : null}
      {toolbar}
      {belowToolbar ? <div className="rr-table-shell__below-toolbar">{belowToolbar}</div> : null}
      {body}
      {footer}
    </Stack>
  );

  if (noChrome) {
    return (
      <div {...dom} ref={ref} {...base.attrs} className={base.className} style={base.style}>
        {inner}
      </div>
    );
  }

  return (
    <Card {...dom} ref={ref as Ref<HTMLElement>} withBorder {...base.attrs} className={base.className} style={base.style}>
      {inner}
    </Card>
  );
});
